import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { addDocument, findDocumentsByField } from '../services/firestoreService';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Input,
} from '@mui/material';

const PatientsImporter = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    setError('');
    setSuccess('');
    setProgress(0);
    setFile(e.target.files?.[0] || null);
  };

  const getValueByCandidates = (row, candidates) => {
    const keys = Object.keys(row || {});
    for (const candidate of candidates) {
      const foundKey = keys.find((k) => k.trim().toLowerCase() === candidate.toLowerCase());
      if (foundKey) return row[foundKey];
    }
    // Fallback: startsWith
    for (const candidate of candidates) {
      const foundKey = keys.find((k) => k.trim().toLowerCase().startsWith(candidate.toLowerCase()));
      if (foundKey) return row[foundKey];
    }
    return '';
  };

  const importRows = async (rows) => {
    if (!rows || rows.length === 0) {
      setError('El archivo no contiene filas de datos.');
      setLoading(false);
      return;
    }

    let imported = 0;
    let duplicates = 0;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        const ficha = String(getValueByCandidates(row, ['ficha']) || '').trim();
        const name = String(getValueByCandidates(row, ['nombre_paciente', 'nombre paciente', 'name']) || '').trim();
        const rutTutor = String(getValueByCandidates(row, ['rut_tutor', 'rut tutor', 'rut']) || '').trim();
        const nombreTutor = String(getValueByCandidates(row, ['nombre_tutor', 'nombre tutor']) || '').trim();
        const especie = String(getValueByCandidates(row, ['especie', 'species']) || '').trim();
        const breed = String(getValueByCandidates(row, ['raza', 'breed']) || '').trim();
        const edad = String(getValueByCandidates(row, ['edad', 'age']) || '').trim();

        if (!name && !ficha) {
          // Fila vacía: la saltamos
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        // Clave única: ficha (si existe) o name
        const duplicateKey = ficha || name;
        const duplicateField = ficha ? 'ficha' : 'name';
        const existing = await findDocumentsByField('patients', duplicateField, duplicateKey);
        if (existing && existing.length > 0) {
          duplicates += 1;
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        // Buscar el tutor en owners por RUT para obtener su ID
        let ownerId = '';
        if (rutTutor) {
          const ownerResults = await findDocumentsByField('owners', 'rut', rutTutor);
          if (ownerResults && ownerResults.length > 0) {
            ownerId = ownerResults[0].id;
          }
        }

        const doc = {
          ficha,
          name,
          rutTutor,
          nombreTutor,
          species: especie,
          breed,
          edad,
          ownerId,
          createdAt: new Date(),
        };

        await addDocument('patients', doc);
        imported += 1;
        setProgress(Math.round(((i + 1) / rows.length) * 100));
      } catch (err) {
        console.error('Error procesando fila de pacientes:', row, err);
        setError(`Error al procesar la fila ${i + 1}. La importación se ha detenido.`);
        setLoading(false);
        return;
      }
    }

    const duplicateInfo = duplicates
      ? ` (${duplicates} filas omitidas por duplicado de Ficha o Nombre)`
      : '';
    setSuccess(`${imported} de ${rows.length} filas importadas con éxito.${duplicateInfo}`);
    setLoading(false);
  };

  const processImport = async () => {
    if (!file) {
      setError('Por favor, selecciona un archivo Excel o CSV.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    const fileName = file.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const rows = results.data || [];
          await importRows(rows);
        },
        error: (err) => {
          console.error('Error al leer el archivo CSV de pacientes:', err);
          setError('Error al leer el archivo CSV: ' + err.message);
          setLoading(false);
        },
      });
      return;
    }

    if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          await importRows(rows);
        } catch (err) {
          console.error('Error al procesar el archivo Excel de pacientes:', err);
          setError('Error al procesar el archivo Excel. Revisa el formato.');
          setLoading(false);
        }
      };
      reader.onerror = (err) => {
        console.error('Error de lectura del archivo Excel:', err);
        setError('No se pudo leer el archivo Excel.');
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    setError('Formato de archivo no soportado. Usa .xlsx, .xls o .csv.');
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Importar Pacientes desde Excel
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        El archivo debe tener las columnas: <strong>FICHA</strong>, <strong>NOMBRE_PACIENTE</strong>, <strong>RUT_TUTOR</strong>, <strong>NOMBRE_TUTOR</strong>, <strong>ESPECIE</strong>, <strong>RAZA</strong> y <strong>EDAD</strong>.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Importante:</strong> Se detectan duplicados por el campo <strong>FICHA</strong> (o <strong>NOMBRE_PACIENTE</strong> si no hay ficha). Los pacientes ya existentes no se duplicarán.
      </Alert>
      <Box>
        <Input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          onClick={processImport}
          disabled={loading || !file}
        >
          {loading ? 'Importando...' : 'Importar Pacientes'}
        </Button>
      </Box>
      {loading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default PatientsImporter;
